<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div v-if="initialLoading" class="d-flex justify-content-center align-items-center min-height-200">
            <busy-spinner text="Loading..."></busy-spinner>
        </div>

        <div v-if="shouldRenderReportArea" class="">  <!-- REPORT AREA if displayMode === 'report' -->
            <car-pdf 
                v-if="reportInputsReady"
                title="Corrective Action Report"
                :task-id="taskId"
                :task-data="task"
                :form-version-data="formVersionData"
                :workflow-files="workflowFiles"
                :extra-images="reportImages"
            ></car-pdf>
        </div>

        <div 
            v-show="shouldShowFormArea"
            v-if="shouldRenderFormArea"
            class=""
        >
            <!-- <div class="">
                <h5>Form Version Data:</h5>
                <pre>{{ formVersionData }}</pre>
                <hr>
                <h5>Submission Data:</h5>
                <pre>{{ submissionData }}</pre>
            </div> -->
            
            <div class="">  <!-- FORM AREA -->
                <div v-if="formData">
                    <h3>{{ formData.title }}</h3>
                </div>
                <!-- Do not show this component until you have the schema -->
                <formio-form
                    ref="formioFormComp"
                    v-if="formCanRender"
                    :schema="schema"
                    :submission-data="existingData"
                    :file-urls="fileUrls"
                    :form-options="formOptions"
                    :html-mode="htmlMode"
                    :read-only="readOnly"
                    :parent-id="taskId"
                    :parent="task"
                    :render-all-extras="formioRenderAllExtras"
                    @setup-complete="handleFormSetupComplete"
                    @change="handleFormChange"
                    @error="handleFormError"
                    @is-valid="handleFormIsValid"
                    @fishbone-image="handleFishboneImage"
                    class="mt-3"
                ></formio-form>
            </div>

            <div 
                v-if="formSetupComplete"
                class="mt-4"
            >  
                <hr>
                <div
                    v-if="canTuSubmitForm"
                    key="formSubmitButtons"
                    class="d-flex gap-3 align-items-center justify-content-between"
                >
                    <!-- VALIDATION STATUS -->
                    <div class="">
                        <div 
                            v-if="!localIsValid"
                            key="formNotValid"
                            class="flex-shrink-1 text-left text-danger-400 font-size-70 font-weight-bold text-uppercase"
                        >
                            <b-icon icon="x-circle-fill" class="mr-1"></b-icon>
                            Form Incomplete
                        </div>
                    </div>
                    <!-- SUBMIT BUTTONS -->
                    <div class="d-flex gap-4 align-items-center justify-content-end">
                        <b-button 
                            v-if="showSaveDraftButton"
                            size="lg" 
                            variant="subtle-secondary"
                            class="min-width-150"
                            @click="submitDraft({showToast: true})"
                        >
                            <span v-if="loadingSave" key="loadingSave">
                                <b-spinner small></b-spinner>
                            </span>
                            <span v-else key="notLoadingSave">
                                Save Draft
                            </span>
                        </b-button>
                        <b-button 
                            v-if="showSavePartialSubmissionButton"
                            size="lg" 
                            variant="subtle-secondary"
                            class="min-width-150"
                            :disabled="disableSavePartialSubmissionButton"
                            @click="submitPartialForm({showToast: true})"
                        >  
                            <span v-if="loadingSave" key="loadingSave">
                                <b-spinner small></b-spinner>
                            </span>
                            <span v-else key="notLoadingSave">
                                Save Progress
                            </span>
                        </b-button>
                        <b-button 
                            v-if="showSaveCompleteSubmissionButton"
                            size="lg" 
                            variant="primary"
                            class="min-width-150"
                            :disabled="disableSaveCompleteSubmissionButton"
                            @click="submitCompleteForm({showToast: true})"
                        >  
                            <span v-if="loadingSave" key="loadingSave">
                                <b-spinner small></b-spinner>
                            </span>
                            <span v-else key="notLoadingSave">
                                Update Data
                            </span>
                        </b-button>
                    </div>
                </div>
                <div v-else key="hideSubmitButtons">
                    <div class="font-weight-bold text-uppercase text-muted font-size-80">
                        Read Only
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
// This does the API calls around a Formio form -- not the actual rendering of the form

import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
//import merge from 'lodash/merge';
import FormioForm from './FormioForm.vue';

export default {
    name: 'WorkflowFormFormio',
    components: {
        FormioForm,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        taskId: {
            type: String,
            required: true,
        },
        displayMode: {
            type: String,
            default: 'input',  // options should be 'input' (form), 'display' (read-only form?), or 'report' (will default to something else if no report here)
        }
    }, 
    data: function() {
        return {
            // OUR BACKEND DATA
            formCanRender: false,  // when you have ALL THINGS needed to display form (schema AND submission) then set this true to show the formio-form component
            formSetupComplete: false,  // when you get @setup-complete event from formio-form component, set this to true so we're ready to go
            initialLoading: false,
            task: null,  // full task object, set from API
            existingData: null,  // after we figure out if we should use task.data or task.draft_data
            workflowFiles: [],  // stashed array of all TaskFiles in the workflow, so we can pass to report component also
            fileUrls: {},  // mapping of file.id (in our backend format) and the updated download URL
            // not used - existingSubmittedData: null,  // from task.data -- the SUBMITTED data
            // not used - existingDraftData: null,  // if we had anything in task.draft_data
            formVersionData: null,  // set from API
            schema: null,  // will get from API, pull out of formVersionData - NEW Feb 4 2022: will also merge in task data (and maybe workflow context later) so its all available to the form
            formData: null,  // the parent of form_version -- from API
            // FORMIO CONFIG
            formOptions: {
                alwaysDirty: false,  // have not found any benefit of setting alwaysDirty to true yet
            },  // any EXTRA ones... we handle readOnly and renderMode separately
            htmlMode: false,  // to show the form as not a form
            readOnly: false,  // can set this separately from htmlMode (this just disables the form)
            // NEW SUBMISSION DATA
            currentSubmissionData: {},  // set it in handlers for @events from the form
            localIsValid: null,  // set in in handlers for @events from the form
            // SAVING
            loadingSave: false,  // when submitting final data, or draft
            // REPORT RELATED THINGS
            reportImages: {},  // should start as empty object, use this.$set to add things, key = the key used in pdf 'image' object, value is either URL, or dataURL
        }
    },
    computed: {
        shouldRenderReportArea() {
            // check a bunch of things to see if we should be showing report
            return (this.displayMode === 'report') && !!this.task?.id;
        },
        formioRenderAllExtras() {
            // pass this to render-all-extras prop on formio component -- force shows the fishbone so we can grab it
            return this.shouldRenderReportArea;
        },
        reportInputsReady() {
            // check that everything we need to START building the PDF report is ready
            const haveTaskAndFormVersionData = !!this.task?.id && !!this.formVersionData?.id;
            // also need to make sure we have anything we need from the rendered form (images, 'hydrated' components, etc.)
            return haveTaskAndFormVersionData && this.formSetupComplete;
        },
        shouldShowFormArea() {
            // controls v-show on the entire form / submit buttons / etc.
            // you need to RENDER that even for showing report, but not SHOW it - so this can be false while shouldRenderForm() can be true
            return (this.displayMode !== 'report');
        },
        shouldRenderFormArea() {
            // see notes above -- controls v-if on the form / submit buttons / etc.
            return true;  // I think just always true for now?
        },
        isActive() {
            return this.task?.sm_state === 'active';
        },
        isCompleted() {
            return this.task?.sm_state === 'completed';
        },
        currentStateCanEdit() {  // based on current state, can assignees edit?
            return this.task?.can_edit_data_now;  // OLD this.task?.state_dict?.assignee_can_edit;
        },
        canTuSubmitForm() {
            // gets from model method .can_tu_submit_form() which checks the TU in serializer
            return this.task?.can_tu_submit_form;  // this also checks task.can_edit_data_now on backend
        },
        showSaveDraftButton() {
            // should we show the 'Save Draft' or 'Save Progress' button?
            // only show if NOT YET COMPLETED -- because if its already completed, we really only want to deal with full updates all the way to submit, not saving drafts
            //return !this.isCompleted && this.canTuSubmitForm;
            // NEW Feb 21 2022 - added field 'allow_draft_submissions' (which is likely always false)
            // instead, we added 'allow_partial_submissions' -- will run through all things on form, only get the fields that have values and are valid, send that
            // that's basically like a draft... but data will get merged to workflow.meta (if needed) on backend
            const allowDrafts = this.task?.allow_draft_submissions;
            return allowDrafts && !this.isCompleted && this.canTuSubmitForm;
        },
        showSavePartialSubmissionButton() {
            // for showing the 'save progress' button
            // only allowed when backend says task.allow_partial_submissions (based on state of task, must not be completed)
            // must also check this.canTuSubmitForm
            // also, to avoid user confusion we will only show this when the form is NOT COMPLETED
            // otherwise, we'll show the saveCompleteSubmission button if task is already completed
            // this means user will only do partial submissions if they save progress, until they click on Complete Task, which submits full submission
            // make sure your backend and frontend validation are in sync!  otherwise save partial might get accepted, frontend shows all is valid, user tries to complete task then they get error from backend... not good
            return this.task?.allow_partial_submissions && !this.isCompleted && this.canTuSubmitForm;
        },
        disableSavePartialSubmissionButton() {
            // only applies when the save partial button is showing
            // don't have to check if its valid... because this will run through everything on form and ONLY save the things that have a value and are valid
            return false;  // for now just never disable it... we'll only check if we have any data when we click button because its expensive to calculate
        },
        showSaveCompleteSubmissionButton() {
            // should we show the 'Save & Submit' button?  
            // ONLY show this if TASK was already completed, but we are allowing edits outside of normal task 'active' state
            // because we'll show the Complete Task button instead, which will also submit the form first
            // OR we could only show this button, and make it complete the task... only one
            // OLD return this.isCompleted && this.canTuSubmitForm;
            // NEW - Feb 21 2022 - removing ability to save drafts for most things,
            //      instead, we'll show EITHER the savePartialSubmission button (if task is NOT YET COMPLETE)
            //          or we'll show this button if task is completed already, and form is valid 
            // allow user to save a submitted submission but not complete the task... that's basically like a draft, but without the confusion around which data is correct (especially when linked with workflow data - workflow.meta)
            return this.isCompleted && this.canTuSubmitForm;
        },
        disableSaveCompleteSubmissionButton() {
            // only applies whtn the saveCompleteSubmission button is showing
            // show the button but disable it when the data is not valid
            // we do not allow partial submissions if task is completed (for now)
            return !this.localIsValid;
        },
        assignedTuIds() {
            const assignedTus = this.task?.assigned_tus;
            if (assignedTus && Array.isArray(assignedTus)) {
                return this.task.assigned_tus.map(tu => tu.id);
            } else {
                return []
            }
        },
        currentTuIsAssignee() {
            return this.assignedTuIds.includes(this.$currentUserInfo.tenant_user_id);
        },
    },
    created() {
    },
    async mounted() {
        //await this.getFormSchema();
        await this.loadInitial();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async loadInitial() {
            this.initialLoading = true;
            try {
                // fetches the schema and any existing submissions for this task from our backend
                const { data } = await suppliosApi.workflowTask.get_form_data(this.taskId);
                // will have { task, form_version, form, workflow_context, files } full objects
                this.setTask(data.task);
                this.setFiles(data.files);
                this.setSchema(data.form_version, data.task, data.workflow_context);
                this.setFormVersionData(data.form_version);
                this.setFormData(data.form);
                this.formCanRender = true;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.initialLoading = false;
        },
        setTask(taskObj) {
            // pull out any things you need, and set this.task
            this.task = taskObj;  // watch out for using computeds that are based on this... may need nextTick
            // ALSO - replace file URLs, and any other prep-work for the submission data before passing to actual form
            // FEB 23 2022 - we now get ALL files in the workflow, handle them separately, not here
            //const taskFiles = taskObj.files ?? [];  // array of any task files
            //const filesById = Object.fromEntries(taskFiles.map(file => ([ file.id, file.download_url ])));  // pull out the download_url, to replace the one stored in form submission data as 'url'
            //this.fileUrls = Object.assign({}, filesById);
            // then we pass this.fileUrls to the child formio-form component, which replaces them for the old URLs when it sets submission
            console.log('from setTask, here is taskObj', taskObj);
            /* let submittedData = cloneDeep((taskObj.data ?? {}));  // we MUTATE this below...
            const draftData = taskObj.draft_data ?? {};  // don't need to cloneDeep... not mutating this one in merge
            this.existingData = merge(submittedData, draftData); */
            // NEW - based on task submission objects now, with draft/submitted already merged on the backend
            this.existingData = cloneDeep(taskObj.submission_data);
            // TODO - come back to this once you know how to handle things on backend
            // ALSO - figure out what state the form should be in
            // if not yet completed but current TU cannot edit, display it as read-only
            // if not yet completed but current TU CAN edit, show it as normal form
            // if IS completed, but TU CAN edit, then we're allowing edits in extra states, so show as normal form (NEW on Feb 18 2022)
            // and for now we are NOT USING htmlMode -- just always keep it false for now -- it displays things strangely
            this.htmlMode = false;  // always for now
            this.readOnly = !this.canTuSubmitForm;
            /* OLD STUFF - but as of Feb 18 2022 it only depends on this.canTuSubmitForm
            if (taskObj.is_completed) {
                this.htmlMode = false;  // htmlMode is actually not great... does not display dates correctly, does not hide all buttons, file dnd areas, etc... just set readOnly = true
                this.readOnly = true;  // ???
            } else {
                if (this.currentStateCanEdit && this.currentTuIsAssignee) {  // maybe change to new this.canTuSubmitForm
                    this.htmlMode = false;
                    this.readOnly = false;
                } else {
                    this.htmlMode = false;
                    this.readOnly = true;
                }
            }*/
        },
        setFiles(fileList) {
            // we get an array of ALL the TaskFiles in the workflow - maybe later we'll get only the ones we need
            if (fileList && Array.isArray(fileList)) {
                this.workflowFiles = fileList;  // stash it so we can pass to report component too
                const filesById = Object.fromEntries(fileList.map(file => ([ file.id, file.download_url ])));  // pull out the download_url, to replace the one stored in form submission data as 'url'
                this.fileUrls = Object.assign({}, filesById);
            } else {
                this.fileUrls = {};
                this.workflowFiles = [];
            }
        },
        setSchema(formVersionData, taskData, workflowContext) {
            // set the final schema we pass to the form -- we will merge some things together
            // in future, you may want to do this on backend for more consistency across form components?
            const schema = cloneDeep(formVersionData.schema);
            // now add in the full task object, also under 'customData' - don't replace entire customData key in schema... you'll likely already have things there - use lodash set
            const taskDataClone = cloneDeep(taskData);  // we clone in it case it get's mutated in here, we don't want to screw up the form -- form will only take the original task data
            set(schema, 'customData.task', taskDataClone);
            const workflowContextClone = cloneDeep(workflowContext);
            set(schema, 'customData.workflow_context', workflowContextClone);
            this.schema = schema;
        },
        setFormVersionData(formVersionData) {
            // set local data from API response
            // MOVED TO SEPARATE METHOD - this.schema = formVersionData.schema;
            this.formVersionData = formVersionData;
        },
        setFormData(formData) {
            // set local data from API response
            this.formData = formData;
        },
        handleFormChange(newData) {
            // 'change' event emits changed payload - includes { changed, data, isValid }
            // but we are now just passing in the 'data' part of it -- other stuff seems unreliable
            // changed is info about the component that changed, data is the overall submission data, isValid is validity of that data
            //const { data, isValid } = formChangedPayload;  
            // just update our local copy 
            this.currentSubmissionData = newData;  // clone it?  use formio fast cloneDeep?
            //this.localIsValid = isValid;
        },
        handleFormError() {  // errors - not using payload so ignore it for now
            // handler from formio-formm component
            console.log('from handleFormError');
        },
        handleFormIsValid(isValid) {
            // handler from formio-formm component - $emit to parent also!
            this.localIsValid = isValid;
            this.$emit('is-valid', isValid);
        },
        handleFormSetupComplete() {
            // from @setup-complete event from formio-form component - your own event to tell this component the form is ready to go
            console.log('Formio form is ready to go');
            this.formSetupComplete = true;
        },
        getFormioFormRef() {
            // return the FormioForm component ref, will be undefined if we can't find it
            return this.$refs['formioFormComp'];
        },
        async submitOnTaskCompleted() {
            // CALLED FROM PARENT - WorkflowTask - just call the needed method here
            // parent expects to return success boolean
            return await this.submitCompleteForm();
        },
        async submitCompleteForm({showToast = false} = {}) {  // CAN ALSO CALL THIS FROM PARENT USING REF! - maybe ONLY from parent... maybe only have 'save draft' on this one
            // call validation method first?
            // GRAB FORM DATA BEFORE STARTING LOADING -- loading will destroy form in child component???
            // UPDATED Feb 23 2022 - not just using the submission data from formio events -- grabbing it ourselves so we can ignore read-only (disabled fields, etc.)
            // GRAB FORM DATA BEFORE STARTING LOADING -- loading will destroy form in child component???
            // must get PARTIAL data here from FormioForm component -- we'll only get it when called, because it might be expensive
            const formComp = this.getFormioFormRef();
            const isValid = formComp ? formComp.checkValidationBeforeCompleteSubmit() : false;
            if (!isValid) {
                console.log('form is not valid');
                await this.showBlockedSubmitMessage('Some items in this form are not valid.  Please correct the errors.');
                return false;
            }
            const submitData = formComp ? cloneDeep(formComp.getCompleteSubmissionData()) : null;
            const hasDataToSubmit = !!submitData && !isEmpty(submitData);
            if (!hasDataToSubmit) {
                console.log('form has no data');
                await this.showBlockedSubmitMessage();
                return false;
            }
            //const submitData = cloneDeep(this.currentSubmissionData);
            this.loadingSave = true;
            let success = false;  // we'll return this so caller knows status
            try {
                const saveResp = await suppliosApi.workflowTask.submit_complete_form(this.taskId, submitData);
                const taskObj = saveResp.data;  // returns the full task object
                // emit to parent right away -- it can decide to do something or not
                this.$emit('complete-form-submitted', taskObj);
                this.setTask(taskObj);
                // HANDLE STATE CHANGE??? OR OTHER BIG THINGS TO EMIT TO PARENT?
                if (showToast) {
                    this.$toast.success('Submission saved.');
                }
                // show a different success message / page?
                success = true;
            } catch (err) {
                this.apiErrorHandler(err);
                success = false;
            }
            this.loadingSave = false;
            return success;
        },
        async showBlockedSubmitMessage(msg, title) {
            const finalMsg = msg || 'There is not data to submit yet.';
            const finalTitle = title || 'Unable to submit';
            return await this.$bvModal.msgBoxOk(finalMsg, {
                title: finalTitle,
                centered: true,
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'primary',
                headerClass: 'p-2 border-bottom-0',
                footerClass: 'p-2 border-top-0',
            });  // will always return true
        },
        async showPartialDataSaveConfirm() {
            const confirmMsg = 'Saving progress does NOT complete this task -- it only saves your existing data so you can finish later.  Should we proceed?';
            const proceed = await this.$bvModal.msgBoxConfirm(confirmMsg, {
                title: 'Save progress?',
                centered: true,
                size: 'sm',
                buttonSize: 'sm',
                okTitle: 'Proceed',
                cancelTitle: 'Cancel',
                okVariant: 'primary',
                cancelVariant: 'minimal-secondary',
                headerClass: 'p-2 border-bottom-0',
                footerClass: 'p-2 border-top-0',
            });
            return proceed;  // its a boolean, will be true if they click OK, false if they cancel, null if they close modal
        },
        async submitPartialForm({showToast = false} = {}) {  // CAN ALSO CALL THIS FROM PARENT USING REF! - maybe ONLY from parent... maybe only have 'save draft' on this one
            // call validation method first?
            // GRAB FORM DATA BEFORE STARTING LOADING -- loading will destroy form in child component???
            // must get PARTIAL data here from FormioForm component -- we'll only get it when called, because it might be expensive
            const formComp = this.getFormioFormRef();
            const submitData = formComp ? cloneDeep(formComp.getPartialSubmissionData()) : null;
            const hasDataToSubmit = !!submitData && !isEmpty(submitData);
            if (!hasDataToSubmit) {
                await this.showBlockedSubmitMessage();
                return false;
            }
            const shouldProceed = await this.showPartialDataSaveConfirm();
            if (!shouldProceed) {
                return false; 
            }  // otherwise, keep going
            this.loadingSave = true;
            let success = false;  // we'll return this so caller knows status
            try {
                console.log('partial submitData', submitData);
                const saveResp = await suppliosApi.workflowTask.submit_partial_form(this.taskId, submitData);
                const taskObj = saveResp.data;  // returns the full task object
                // emit to parent right away -- it can decide to do something or not
                this.$emit('partial-form-submitted', taskObj);
                this.setTask(taskObj);
                // HANDLE STATE CHANGE??? OR OTHER BIG THINGS TO EMIT TO PARENT?
                if (showToast) {
                    this.$toast.success('Progress saved');
                }
                // show a different success message / page?
                success = true;
            } catch (err) {
                this.apiErrorHandler(err);
                success = false;
            }
            this.loadingSave = false;
            return success;
        },
        async submitDraft({showToast = false} = {}) {
            // any validation???
            // GRAB FORM DATA BEFORE STARTING LOADING -- loading will destroy form in child component???
            const submitData = cloneDeep(this.currentSubmissionData);
            this.loadingSave = true;
            let success = false;  // we'll return this so caller knows status
            try {
                const saveResp = await suppliosApi.workflowTask.submit_draft(this.taskId, submitData);
                const taskObj = saveResp.data;  // returns the full task object
                // emit to parent right away -- it can decide to do something or not
                this.$emit('draft-form-submitted', taskObj);
                this.setTask(taskObj);
                // probably nothing major to $emit to parent for saving drafts?  but maybe should if parent also has all submission data?
                if (showToast) {
                    this.$toast.success('Draft saved.');
                }
                success = true;
            } catch (err) {
                this.apiErrorHandler(err);
                success = false;
            }
            this.loadingSave = false;
            return success;
        },
        // EXTRAS
        handleFishboneImage(fishboneDataURL) {
            // formio-form will emit $emit('fishbone-image', fishboneDataURL);  // emit the image dataURL as payload 
            // put it in 'reportImages' here - under 'fishbone' key?
            if (fishboneDataURL) {
                console.log('just set fishbone dataURL in reportImages');
                this.$set(this.reportImages, 'fishbone', fishboneDataURL);
            }
        }
    }
}
</script>

<style>

</style>